// External pages and third-party sign-ins (Google Calendar, Gmail, Notion, ClickUp,
// bot connections, linking a sign-in method).
//
// In a browser nothing changes. In the app, Google refuses sign-in inside the app's own
// web view, and the page that finishes a sign-in needs the session the app holds, so:
//   1. the app opens  WEB_URL/native-auth?to=<provider url>  in the system browser
//      (SFSafariViewController / Chrome Custom Tab); that page marks the tab and goes on
//   2. the provider sends the person back to the usual callback page on busybee.day
//   3. that page sees the mark and hands its address to the app:
//      day.busybee.app://oauth-return/<path>?<query>
//   4. the app closes the browser and opens the same callback page itself, where the
//      session is, and the page finishes the sign-in as on the web
// Steps 1 and 3 run before React starts (handleAuthBounce, called from index.js).
import { Browser } from '@capacitor/browser';
import { APP_SCHEME, WEB_URL, isNative } from './platform';

const MARK = 'bb_native_auth';
const RETURN_PREFIX = `${APP_SCHEME}://oauth-return`;
// Providers the bounce page forwards to straight away; anything else needs a tap.
const TRUSTED_HOSTS = ['accounts.google.com', 'api.notion.com', 'app.clickup.com', 'appleid.apple.com'];

export async function openExternal(url) {
  if (isNative()) {
    await Browser.open({ url });
  } else {
    window.open(url, '_blank', 'noopener');
  }
}

export async function startExternalAuth(url) {
  if (isNative()) {
    await Browser.open({ url: `${WEB_URL}/native-auth?to=${encodeURIComponent(url)}` });
  } else {
    window.location.assign(url);
  }
}

// The app was opened with day.busybee.app://oauth-return/<path>: the path to open in it.
export function returnPathFromAppUrl(appUrl) {
  if (!appUrl || !appUrl.startsWith(RETURN_PREFIX)) return null;
  const rest = appUrl.slice(RETURN_PREFIX.length);
  return rest.startsWith('/') ? rest : `/${rest}`;
}

export async function closeAuthBrowser() {
  try { await Browser.close(); } catch { /* already closed (Android closes it itself) */ }
}

// Runs in the system browser, before React. Returns true when the page is leaving.
export function handleAuthBounce() {
  if (isNative() || typeof window === 'undefined') return false;
  const { pathname, search } = window.location;
  let marked = false;
  try { marked = sessionStorage.getItem(MARK) === '1'; } catch { /* storage off */ }

  if (pathname === '/native-auth') {
    const to = new URLSearchParams(search).get('to') || '';
    let target;
    try { target = new URL(to); } catch { return false; }
    if (target.protocol !== 'https:') return false;
    try { sessionStorage.setItem(MARK, '1'); } catch { /* storage off */ }
    if (TRUSTED_HOSTS.includes(target.hostname)) {
      window.location.replace(target.href);
    } else {
      // A bot connection's own sign-in server: shown, then followed on a tap.
      document.body.innerHTML = '';
      const link = document.createElement('a');
      link.href = target.href;
      link.textContent = `${target.hostname} →`;
      link.style.cssText = 'display:block;margin:40vh auto 0;text-align:center;font:600 17px system-ui;color:#F59E0B';
      document.body.style.background = '#0a0a0b';
      document.body.appendChild(link);
    }
    return true;
  }

  if (marked) {
    try { sessionStorage.removeItem(MARK); } catch { /* storage off */ }
    window.location.replace(`${RETURN_PREFIX}${pathname}${search}`);
    return true;
  }
  return false;
}
